import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import API_CONFIG from '../../config/api';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import { avaliarPermissaoEtapaProcesso } from '../../pages/contratos/utils/processoEtapaPermissao';
import {
  calcularTransicoesPermitidas,
  destinoPadraoParaAcao,
  isTransicaoPadrao,
  ordenarEtapas,
} from '../../pages/contratos/utils/processoTransicoes';

const STATUS_LABELS = {
  EM_ANDAMENTO: 'Em andamento',
  EM_ANALISE: 'Em análise',
  REJEITADO: 'Rejeitado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

const ACAO_BOTOES = [
  {
    value: 'DEVOLVER',
    label: 'Devolver',
    hint: 'Volta uma etapa',
    icon: <UndoIcon fontSize="small" />,
    color: '#ea580c',
    bg: 'rgba(234,88,12,0.08)',
    border: 'rgba(234,88,12,0.35)',
  },
  {
    value: 'REJEITAR',
    label: 'Rejeitar',
    hint: 'Retorna ao início',
    icon: <ThumbDownIcon fontSize="small" />,
    color: '#dc2626',
    bg: 'rgba(220,38,38,0.08)',
    border: 'rgba(220,38,38,0.35)',
  },
  {
    value: 'APROVAR',
    label: 'Autorizar',
    hint: 'Avança o fluxo',
    icon: <ThumbUpIcon fontSize="small" />,
    color: '#059669',
    bg: 'rgba(5,150,105,0.1)',
    border: 'rgba(5,150,105,0.4)',
  },
];

export default function AtualizarProcessoModal({
  open,
  onClose,
  processo: processoProp,
  processoId: processoIdProp,
  fluxo: fluxoProp,
  fluxos,
  user,
  usuarioEmail,
  usuarioTipo,
  usuarioSetor,
  onSuccess,
  onBeforeSubmit,
  podeGerenciarProcessos = true,
  podePularEtapas = false,
  usuarios = [],
}) {
  const [processo, setProcesso] = useState(processoProp || null);
  const [fluxo, setFluxo] = useState(fluxoProp || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acao, setAcao] = useState('APROVAR');
  const [etapaDestinoId, setEtapaDestinoId] = useState('');
  const [observacao, setObservacao] = useState('');
  const [anexoFiles, setAnexoFiles] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const [usuariosLocal, setUsuariosLocal] = useState(usuarios);
  const anexoInputRef = React.useRef(null);

  const processoId = processoProp?.id || processo?.id || processoIdProp;

  useEffect(() => {
    if (Array.isArray(usuarios) && usuarios.length) {
      setUsuariosLocal(usuarios);
      return;
    }
    if (!open) return;
    apiFetch('/api/usuarios')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setUsuariosLocal(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [open, usuarios]);

  useEffect(() => {
    if (!open || !processoId) return;
    setProcesso(processoProp || null);
    // Não inicializar com fluxoProp da aba Indicadores — espera resolução pelo fluxoId do processo
    setFluxo(null);
    setAttempted(false);
    if (processoProp) {
      setAcao('APROVAR');
      setObservacao('');
      setAnexoFiles([]);
      const fluxoId =
        processoProp.fluxoId ??
        processoProp.fluxo_id ??
        processoProp.fluxo?.id ??
        null;
      apiFetch('/api/contratos/fluxos')
        .then((r) => (r.ok ? r.json() : []))
        .then((fluxosData) => {
          let fluxoEncontrado =
            Array.isArray(fluxosData) && fluxoId
              ? fluxosData.find((f) => Number(f.id) === Number(fluxoId))
              : null;
          if (!fluxoEncontrado && Array.isArray(fluxosData)) {
            const tipo = (processoProp?.tipoFluxo || 'CONTRATACAO').toUpperCase();
            if (tipo === 'MINUTA') {
              fluxoEncontrado = fluxosData.find((f) => (f.nome || '').toLowerCase().includes('minuta'));
            } else if (tipo === 'ADITIVO') {
              fluxoEncontrado = fluxosData.find((f) => (f.nome || '').toLowerCase().includes('aditivo'));
            } else {
              fluxoEncontrado = fluxosData.find((f) => {
                const n = (f.nome || '').toLowerCase();
                return n.includes('contratação') || n.includes('contratacao');
              });
            }
          }
          // Nunca preferir fluxoProp da aba Indicadores se o processo tem fluxo próprio
          if (!fluxoEncontrado && fluxoProp && fluxoId && Number(fluxoProp.id) === Number(fluxoId)) {
            fluxoEncontrado = fluxoProp;
          }
          if (fluxoEncontrado) setFluxo(fluxoEncontrado);
          else if (fluxoProp && !fluxoId) setFluxo(fluxoProp);
          const etapas = fluxoEncontrado?.etapas || (fluxoId ? [] : fluxoProp?.etapas) || processoProp?.fluxo?.etapas || [];
          const etapaAtualId = processoProp?.etapaAtual?.id || processoProp?.etapa_atual_id;
          const idxAtual = etapas.findIndex((e) => Number(e.id) === Number(etapaAtualId));
          setEtapaDestinoId(etapas[idxAtual + 1]?.id || etapas[0]?.id || '');
        })
        .catch(() => {
          const fluxoIdCatch =
            processoProp.fluxoId ?? processoProp.fluxo_id ?? processoProp.fluxo?.id ?? null;
          const etapas =
            (fluxoIdCatch && Number(fluxoProp?.id) === Number(fluxoIdCatch) ? fluxoProp?.etapas : null) ||
            processoProp?.fluxo?.etapas ||
            [];
          const etapaAtualId = processoProp?.etapaAtual?.id || processoProp?.etapa_atual_id;
          const idxAtual = etapas.findIndex((e) => Number(e.id) === Number(etapaAtualId));
          setEtapaDestinoId(etapas[idxAtual + 1]?.id || etapas[0]?.id || '');
        });
    }
    if (!processoProp && processoId) {
      setLoading(true);
      Promise.all([
        apiFetch(`/api/contratos/processos/${processoId}`).then((r) => (r.ok ? r.json() : null)),
        apiFetch('/api/contratos/fluxos').then((r) => (r.ok ? r.json() : [])),
      ])
        .then(([proc, fluxosData]) => {
          setProcesso(proc);
          const fluxoId = proc?.fluxoId ?? proc?.fluxo_id ?? proc?.fluxo?.id;
          let fluxoEncontrado =
            Array.isArray(fluxosData) && fluxoId
              ? fluxosData.find((f) => Number(f.id) === Number(fluxoId))
              : null;
          if (!fluxoEncontrado && Array.isArray(fluxosData)) {
            const tipo = (proc?.tipoFluxo || 'CONTRATACAO').toUpperCase();
            if (tipo === 'MINUTA') {
              fluxoEncontrado = fluxosData.find((f) => (f.nome || '').toLowerCase().includes('minuta'));
            } else if (tipo === 'ADITIVO') {
              fluxoEncontrado = fluxosData.find((f) => (f.nome || '').toLowerCase().includes('aditivo'));
            } else {
              fluxoEncontrado = fluxosData.find((f) => {
                const n = (f.nome || '').toLowerCase();
                return n.includes('contratação') || n.includes('contratacao');
              });
            }
          }
          setFluxo(fluxoEncontrado);
          const etapas = fluxoEncontrado?.etapas || proc?.fluxo?.etapas || [];
          const etapaAtualId = proc?.etapaAtual?.id || proc?.etapa_atual_id;
          const idxAtual = etapas.findIndex((e) => Number(e.id) === Number(etapaAtualId));
          setEtapaDestinoId(etapas[idxAtual + 1]?.id || etapas[0]?.id || '');
          setAcao('APROVAR');
          setObservacao('');
          setAnexoFiles([]);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open, processoId, processoProp, fluxoProp]);

  const handleAnexoChange = (e) => {
    const selecionados = Array.from(e.target.files || []).filter(
      (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '')
    );
    if (!selecionados.length) {
      if (anexoInputRef.current) anexoInputRef.current.value = '';
      return;
    }
    setAnexoFiles((prev) => {
      const nomes = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const novos = selecionados.filter((f) => !nomes.has(`${f.name}:${f.size}`));
      return [...prev, ...novos].slice(0, 20);
    });
    if (anexoInputRef.current) anexoInputRef.current.value = '';
  };

  const removerAnexo = (index) => {
    setAnexoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAnexo = () => {
    setAnexoFiles([]);
    if (anexoInputRef.current) anexoInputRef.current.value = '';
  };

  const etapas = fluxo?.etapas || [];
  const etapaAtualId = processo?.etapaAtual?.id || processo?.etapa_atual_id;
  const etapaAtualObj =
    processo?.etapaAtual ||
    etapas.find((e) => Number(e.id) === Number(etapaAtualId)) ||
    null;

  const permissao = useMemo(
    () =>
      avaliarPermissaoEtapaProcesso({
        user,
        etapa: etapaAtualObj,
        podeGerenciarProcessos,
        usuarios: usuariosLocal,
      }),
    [user, etapaAtualObj, podeGerenciarProcessos, usuariosLocal]
  );

  const transicoes = useMemo(
    () =>
      calcularTransicoesPermitidas({
        etapas,
        etapaAtualId,
        etapaAtual: etapaAtualObj,
      }),
    [etapas, etapaAtualId, etapaAtualObj]
  );

  const etapasNoSelect = useMemo(() => {
    if (podePularEtapas) return ordenarEtapas(etapas);
    return transicoes.permitidas.length ? transicoes.permitidas : ordenarEtapas(etapas);
  }, [podePularEtapas, etapas, transicoes.permitidas]);

  const pulandoEtapas =
    podePularEtapas &&
    Boolean(etapaDestinoId) &&
    !isTransicaoPadrao(etapaDestinoId, transicoes.idsPermitidos);

  useEffect(() => {
    if (!open || podePularEtapas || !etapaDestinoId) return;
    if (!isTransicaoPadrao(etapaDestinoId, transicoes.idsPermitidos)) {
      const fallback = destinoPadraoParaAcao(acao, transicoes);
      if (fallback) setEtapaDestinoId(fallback);
    }
  }, [open, podePularEtapas, etapaDestinoId, transicoes, acao]);

  const handleSubmit = async () => {
    if (!processo?.id) return;
    if (!permissao.canAct) {
      if (typeof onSuccess === 'function') onSuccess({ error: permissao.mensagem });
      return;
    }
    if (!podePularEtapas && etapaDestinoId && !isTransicaoPadrao(etapaDestinoId, transicoes.idsPermitidos)) {
      if (typeof onSuccess === 'function') {
        onSuccess({ error: 'Transição não permitida. Selecione a etapa seguinte, anterior ou um terminal válido.' });
      }
      return;
    }
    setAttempted(true);
    if (!observacao?.trim()) {
      if (typeof onSuccess === 'function') onSuccess({ error: 'A observação é obrigatória.' });
      return;
    }
    if (!etapaDestinoId) {
      if (typeof onSuccess === 'function') onSuccess({ error: 'Selecione a etapa de destino.' });
      return;
    }
    const destinoId = etapaDestinoId;
    const etapasList = fluxo?.etapas || [];
    const etapaDestino = etapasList.find((e) => Number(e.id) === Number(destinoId)) || null;

    if (typeof onBeforeSubmit === 'function') {
      const proceed = await onBeforeSubmit({
        processo,
        etapaDestino,
        etapaDestinoId: destinoId,
        acao,
        observacao: observacao.trim(),
        anexoFile: anexoFiles[0] || null,
        anexoFiles,
      });
      if (proceed === false) return;
    }

    setSaving(true);
    const voltarInicio = acao === 'REJEITAR';
    try {
      const formData = new FormData();
      formData.append('etapaDestinoId', destinoId);
      formData.append('acao', acao === 'APROVAR' ? 'MOVER' : acao);
      formData.append('observacao', observacao.trim() || '');
      formData.append('usuario', user?.nome || user?.email || usuarioEmail || '');
      formData.append('status', '');
      formData.append('voltarInicio', voltarInicio ? 'true' : 'false');
      if (usuarioEmail) formData.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) formData.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) formData.append('usuarioSetor', usuarioSetor);
      anexoFiles.forEach((file) => formData.append('anexo', file));

      const res = await apiFetch(`/api/contratos/processos/${processo.id}/mover`, {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Erro ao atualizar processo');

      resetAnexo();
      setAttempted(false);
      if (typeof onSuccess === 'function') onSuccess({ data, acao });
      onClose();
    } catch (err) {
      if (typeof onSuccess === 'function') onSuccess({ error: err.message });
    } finally {
      setSaving(false);
    }
  };

  const processoCodigo = processo?.codigo || processo?.id;
  const processoTitulo = processo?.titulo || '';
  const etapaAtualNome =
    processo?.etapaAtual?.nome ||
    etapas.find((e) => Number(e.id) === Number(processo?.etapaAtual?.id || processo?.etapa_atual_id))?.nome;
  const podeAgir = permissao.canAct;

  const docs = (processo?.arquivos || [])
    .filter((a) => a.tipo === 'BUNDLE' || a.tipo === 'FINAL' || a.tipo === 'ASSINADO')
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 28%)',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,64,175,0.9) 50%, rgba(13,148,136,0.82) 100%)',
          color: '#e2e8f0',
          position: 'relative',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <RocketLaunchRoundedIcon sx={{ color: '#5eead4' }} />
              <Typography variant="overline" sx={{ letterSpacing: 1.2, color: '#99f6e4', fontWeight: 800 }}>
                Painel de decisão
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.25 }}>
              Atualizar Processo
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.9, maxWidth: 640 }}>
              {processoCodigo} · {processoTitulo}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              <Chip
                size="small"
                label={STATUS_LABELS[processo?.status] || processo?.status || '—'}
                sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
              />
              {etapaAtualNome && (
                <Chip
                  size="small"
                  label={`Agora: ${etapaAtualNome}`}
                  sx={{ bgcolor: 'rgba(45,212,191,0.2)', color: '#ccfbf1', fontWeight: 700 }}
                />
              )}
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : processo ? (
          <Grid container spacing={2.5}>
            {!podeAgir && permissao.mensagem && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {permissao.mensagem}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, color: '#0f172a' }}>
                1 · Destino
              </Typography>
              <FormControl fullWidth size="small" disabled={!podeAgir || saving}>
                <InputLabel>Nova etapa</InputLabel>
                <Select
                  value={etapaDestinoId}
                  label="Nova etapa"
                  onChange={(e) => setEtapaDestinoId(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: '#fff' }}
                >
                  {etapasNoSelect.map((etapa) => {
                    const idx = transicoes.ordenadas.findIndex((e) => Number(e.id) === Number(etapa.id));
                    const ordem = idx >= 0 ? idx + 1 : etapa.ordem || '';
                    const tag =
                      etapa._transicao === 'proxima'
                        ? ' (próxima)'
                        : etapa._transicao === 'anterior'
                          ? ' (anterior)'
                          : etapa._transicao === 'inicio'
                            ? ' (início)'
                            : etapa._transicao === 'terminal'
                              ? ' (terminal)'
                              : '';
                    return (
                      <MenuItem key={etapa.id} value={etapa.id}>
                        {ordem}. {etapa.nome}
                        {tag}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {pulandoEtapas && (
                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>
                  Você está pulando etapas
                </Alert>
              )}
              {!podePularEtapas && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                  Destinos limitados à próxima, anterior (se permitido) e terminais válidos.
                </Typography>
              )}

              <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2.5, mb: 1, color: '#0f172a' }}>
                2 · Ação
              </Typography>
              <Stack spacing={1} sx={{ opacity: podeAgir ? 1 : 0.5, pointerEvents: podeAgir ? 'auto' : 'none' }}>
                {ACAO_BOTOES.map((btn) => {
                  const selected = acao === btn.value;
                  return (
                    <Paper
                      key={btn.value}
                      elevation={0}
                      onClick={() => {
                        if (!podeAgir) return;
                        setAcao(btn.value);
                        const destino = destinoPadraoParaAcao(btn.value, transicoes);
                        if (destino) setEtapaDestinoId(destino);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        cursor: podeAgir ? 'pointer' : 'not-allowed',
                        border: '2px solid',
                        borderColor: selected ? btn.border : 'rgba(15,23,42,0.08)',
                        bgcolor: selected ? btn.bg : '#fff',
                        transition: 'all 0.15s ease',
                        '&:hover': podeAgir
                          ? { borderColor: btn.border, transform: 'translateY(-1px)' }
                          : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ color: btn.color, display: 'flex' }}>{btn.icon}</Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={800} sx={{ color: btn.color }}>
                            {btn.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {btn.hint}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
              {acao === 'REJEITAR' && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Esta ação retorna o processo para a etapa inicial.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, color: '#0f172a' }}>
                3 · Observação
              </Typography>
              <TextField
                label="Descreva a decisão"
                fullWidth
                multiline
                minRows={4}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Motivo da autorização, devolução ou rejeição..."
                disabled={!podeAgir || saving}
                error={attempted && !observacao?.trim()}
                helperText={
                  attempted && !observacao?.trim()
                    ? 'Informe a observação para prosseguir.'
                    : 'Obrigatória para registrar auditoria da decisão.'
                }
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
              />

              <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2.5, mb: 1, color: '#0f172a' }}>
                4 · Documentos
              </Typography>
              {podeGerenciarProcessos && podeAgir && (
                <Paper
                  elevation={0}
                  onClick={() => !saving && anexoInputRef.current?.click()}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1.5px dashed rgba(14,165,233,0.45)',
                    bgcolor: 'rgba(14,165,233,0.04)',
                    cursor: saving ? 'default' : 'pointer',
                    mb: 1.5,
                    '&:hover': { bgcolor: 'rgba(14,165,233,0.08)' },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <UploadFileOutlinedIcon color="primary" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} color="primary">
                        {anexoFiles.length
                          ? `${anexoFiles.length} PDF(s) selecionado(s) — clique para adicionar mais`
                          : 'Soltar / selecionar PDFs da etapa'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Até 20 PDFs. Serão consolidados com os documentos anteriores desta etapa.
                      </Typography>
                    </Box>
                    {anexoFiles.length > 0 && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetAnexo();
                        }}
                      >
                        Limpar
                      </Button>
                    )}
                  </Stack>
                  {anexoFiles.length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
                      {anexoFiles.map((file, idx) => (
                        <Stack
                          key={`${file.name}-${file.size}-${idx}`}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.5,
                            bgcolor: '#fff',
                            border: '1px solid rgba(15,23,42,0.08)',
                          }}
                        >
                          <PictureAsPdfIcon fontSize="small" color="error" />
                          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap title={file.name}>
                            {file.name}
                          </Typography>
                          <Button size="small" onClick={() => removerAnexo(idx)} disabled={saving}>
                            Remover
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  <input
                    ref={anexoInputRef}
                    type="file"
                    accept="application/pdf"
                    multiple
                    hidden
                    onChange={handleAnexoChange}
                  />
                </Paper>
              )}

              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Consolidações / assinados
                </Typography>
                {docs.map((arq) => {
                  const url =
                    arq.url ||
                    arq.arquivoUrl ||
                    (arq.arquivoPath
                      ? `${API_CONFIG.BACKEND_URL}/${(arq.arquivoPath || '').replace(/^\/+/, '')}`
                      : null);
                  if (!url) return null;
                  const rotulo =
                    arq.tipo === 'FINAL'
                      ? 'Documento final consolidado'
                      : arq.tipo === 'ASSINADO'
                        ? arq.nome || 'Documento assinado'
                        : `Consolidado etapa ${arq.etapaOrdem || arq.etapaId || ''}`;
                  return (
                    <Paper
                      key={arq.id}
                      elevation={0}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 2,
                        border: '1px solid rgba(15,23,42,0.08)',
                        bgcolor: '#fff',
                      }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<PictureAsPdfIcon fontSize="small" />}
                        onClick={() => openAuthenticatedUpload(url)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                      >
                        {rotulo}
                      </Button>
                    </Paper>
                  );
                })}
                {!docs.length && (
                  <Typography variant="caption" color="text.secondary">
                    Nenhum documento anexado ainda.
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Typography color="text.secondary">Processo não encontrado.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(248,250,252,0.9)' }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || loading || !podeAgir}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            px: 2.5,
            borderRadius: 2,
            background: 'linear-gradient(90deg, #1d4ed8, #0d9488)',
          }}
        >
          {saving ? 'Atualizando...' : 'Atualizar Processo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
